import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Tag,
  FileText,
  UsersRound,
  AlertTriangle,
  Settings,
  BarChart3,
  History,
  ShieldCheck,
  HelpCircle,
  ScrollText,
  Bell,
  MessageSquare,
  Star,
  Trophy,
  Gift,
  Megaphone,
} from 'lucide-react';
import { useUIStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { ROUTES } from '@/lib/constants/routes';
import { PERMISSIONS } from '@/lib/constants/permissions';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function createMenuItem(
  key: string,
  label: React.ReactNode,
  icon?: React.ReactNode,
  children?: MenuItem[],
): MenuItem | null {
  return {
    key,
    icon,
    label,
    children: children?.length ? children : undefined,
  } as MenuItem;
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const menuItems: MenuItem[] = [
    createMenuItem('dashboard', 'Dashboard', <LayoutDashboard size={18} />),
    hasPermission(PERMISSIONS.USER_READ)
      ? createMenuItem('users', 'Users', <Users size={18} />)
      : null,

    hasPermission(PERMISSIONS.TRIBE_READ)
      ? createMenuItem('tribes', 'Tribes', <UsersRound size={18} />)
      : null,
    hasPermission(PERMISSIONS.CHALLENGE_READ)
      ? createMenuItem('challenges', 'Challenges', <Trophy size={18} />, [
          createMenuItem('challenges-list', 'All Challenges'),
          createMenuItem('challenges-locations', 'GPS Locations'),
          // createMenuItem('challenges-submissions', 'Submissions'),
          createMenuItem('challenges-wallets', 'Wallets'),
          createMenuItem('challenges-payouts', 'Payouts'),
          createMenuItem('challenges-transactions', 'Transactions'),
        ])
      : null,
    hasPermission(PERMISSIONS.REFERRAL_READ)
      ? createMenuItem('referrals', 'Referrals', <Gift size={18} />)
      : null,
    hasPermission(PERMISSIONS.BANNER_READ)
      ? createMenuItem('banners', 'Banners', <Megaphone size={18} />)
      : null,
    hasPermission(PERMISSIONS.CATEGORY_READ)
      ? createMenuItem('categories', 'Categories', <Tag size={18} />)
      : null,
    hasPermission(PERMISSIONS.CONTENT_READ)
      ? createMenuItem('content', 'Content', <FileText size={18} />, [
          createMenuItem('content-posts', 'All Posts'),
          createMenuItem('content-trending', 'Trending Feed'),
          createMenuItem('content-filter-analytics', 'Filter Analytics'),
          createMenuItem('content-liked-places', 'Liked Places'),
        ])
      : null,
    // hasPermission(PERMISSIONS.CONTENT_READ)
    //   ? createMenuItem('ratings', 'Ratings', <Star size={18} />)
    //   : null,
    // hasPermission(PERMISSIONS.CONTENT_READ)
    //   ? createMenuItem('messaging', 'Messaging', <MessageSquare size={18} />)
    //   : null,
    hasPermission(PERMISSIONS.REPORT_READ)
      ? createMenuItem('reports', 'Reports', <AlertTriangle size={18} />)
      : null,
    hasPermission(PERMISSIONS.NOTIFICATIONS_READ)
      ? createMenuItem('notifications', 'Notifications', <Bell size={18} />, [
          createMenuItem('notifications-templates', 'Templates'),
          createMenuItem('notifications-broadcast', 'Broadcast'),
          // createMenuItem('notifications-logs', 'Delivery Logs'),
        ])
      : null,
    // hasPermission(PERMISSIONS.AUDIT_READ)
    //   ? createMenuItem('analytics', 'Analytics', <BarChart3 size={18} />)
    //   : null,
    hasPermission(PERMISSIONS.POLICY_READ)
      ? createMenuItem('policies', 'Policies', <ScrollText size={18} />)
      : null,
    hasPermission(PERMISSIONS.AUDIT_READ)
      ? createMenuItem('audit', 'Audit Logs', <History size={18} />, [
          createMenuItem('audit-admin', 'Admin Activity'),
          createMenuItem('audit-user', 'User Activity'),
        ])
      : null,
    hasPermission(PERMISSIONS.SUPPORT_READ)
      ? createMenuItem('support', 'Support', <HelpCircle size={18} />)
      : null,
    hasPermission(PERMISSIONS.ADMIN_READ)
      ? createMenuItem(
          'admins',
          'Admins',
          <ShieldCheck size={18} />,
          [
            createMenuItem('admins-list', 'All Admins'),
            hasPermission(PERMISSIONS.ROLE_READ)
              ? createMenuItem('roles', 'Roles & Permissions')
              : null,
          ].filter(Boolean) as MenuItem[],
        )
      : null,

    hasPermission(PERMISSIONS.ADMIN_READ)
      ? createMenuItem('settings', 'Settings', <Settings size={18} />, [
          createMenuItem('settings-age', 'Age Configuration'),
          // createMenuItem('settings-country', 'Country & Region'),
          createMenuItem('settings-location', 'Visibility Rules'),
          // createMenuItem('settings-feed-ranking', 'Feed Ranking'),
          createMenuItem('settings-map-discovery', 'Map Discovery'),
          createMenuItem('settings-location-likes', 'Location Likes'),
          // createMenuItem('settings-check-in', 'Location Heartbeat'),
        ])
      : null,
    // Local-only API testing harness. Deliberately NOT permission-gated: it
    // authenticates with its own test user token rather than the admin session,
    // so admin permissions say nothing about what it can reach.
    // createMenuItem('map-lab', 'Map Lab', <Map size={18} />),
  ].filter(Boolean) as MenuItem[];

  const routeToKey: Record<string, string> = {
    [ROUTES.DASHBOARD]: 'dashboard',
    [ROUTES.USERS]: 'users',
    [ROUTES.CATEGORIES]: 'categories',
    [ROUTES.POSTS]: 'content-posts',
    [ROUTES.TRENDING_POSTS]: 'content-trending',
    [ROUTES.REPORTED_POSTS]: 'content-reported',
    [ROUTES.CONTENT_FILTER_ANALYTICS]: 'content-filter-analytics',
    [ROUTES.CONTENT_LIKED_PLACES]: 'content-liked-places',
    [ROUTES.RATINGS]: 'ratings',
    [ROUTES.TRIBES]: 'tribes',
    [ROUTES.MESSAGING]: 'messaging',
    [ROUTES.REPORTS]: 'reports',
    [ROUTES.NOTIFICATION_TEMPLATES]: 'notifications-templates',
    [ROUTES.NOTIFICATION_BROADCAST]: 'notifications-broadcast',
    [ROUTES.NOTIFICATION_LOGS]: 'notifications-logs',
    [ROUTES.ANALYTICS]: 'analytics',
    [ROUTES.POLICIES]: 'policies',
    [ROUTES.AUDIT_ADMIN]: 'audit-admin',
    [ROUTES.AUDIT_USER]: 'audit-user',
    [ROUTES.SUPPORT]: 'support',
    [ROUTES.ADMINS]: 'admins-list',
    [ROUTES.ROLES]: 'roles',
    [ROUTES.SETTINGS_AGE_CONFIG]: 'settings-age',
    [ROUTES.SETTINGS_COUNTRY_REGION]: 'settings-country',
    [ROUTES.SETTINGS_LOCATION]: 'settings-location',
    [ROUTES.SETTINGS_FEED_RANKING]: 'settings-feed-ranking',
    [ROUTES.SETTINGS_MAP_DISCOVERY]: 'settings-map-discovery',
    [ROUTES.SETTINGS_LOCATION_LIKES]: 'settings-location-likes',
    [ROUTES.SETTINGS_CHECK_IN]: 'settings-check-in',
    [ROUTES.REFERRALS]: 'referrals',
    [ROUTES.BANNERS]: 'banners',
    [ROUTES.CHALLENGES]: 'challenges-list',
    [ROUTES.CHALLENGE_CREATE]: 'challenges-list',
    [ROUTES.GPS_LOCATIONS]: 'challenges-locations',
    [ROUTES.WALLETS]: 'challenges-wallets',
    [ROUTES.PAYOUTS]: 'challenges-payouts',
    [ROUTES.TRANSACTIONS]: 'challenges-transactions',
  };

  const keyToPath: Record<string, string> = {
    dashboard: ROUTES.DASHBOARD,
    'map-lab': ROUTES.MAP_LAB,
    users: ROUTES.USERS,
    categories: ROUTES.CATEGORIES,
    'content-posts': ROUTES.POSTS,
    'content-trending': ROUTES.TRENDING_POSTS,
    'content-reported': ROUTES.REPORTED_POSTS,
    'content-filter-analytics': ROUTES.CONTENT_FILTER_ANALYTICS,
    'content-liked-places': ROUTES.CONTENT_LIKED_PLACES,
    ratings: ROUTES.RATINGS,
    tribes: ROUTES.TRIBES,
    messaging: ROUTES.MESSAGING,
    reports: ROUTES.REPORTS,
    'notifications-templates': ROUTES.NOTIFICATION_TEMPLATES,
    'notifications-broadcast': ROUTES.NOTIFICATION_BROADCAST,
    'notifications-logs': ROUTES.NOTIFICATION_LOGS,
    analytics: ROUTES.ANALYTICS,
    policies: ROUTES.POLICIES,
    'audit-admin': ROUTES.AUDIT_ADMIN,
    'audit-user': ROUTES.AUDIT_USER,
    support: ROUTES.SUPPORT,
    'admins-list': ROUTES.ADMINS,
    roles: ROUTES.ROLES,
    'settings-age': ROUTES.SETTINGS_AGE_CONFIG,
    'settings-country': ROUTES.SETTINGS_COUNTRY_REGION,
    'settings-location': ROUTES.SETTINGS_LOCATION,
    'settings-feed-ranking': ROUTES.SETTINGS_FEED_RANKING,
    'settings-map-discovery': ROUTES.SETTINGS_MAP_DISCOVERY,
    'settings-location-likes': ROUTES.SETTINGS_LOCATION_LIKES,
    'settings-check-in': ROUTES.SETTINGS_CHECK_IN,
    referrals: ROUTES.REFERRALS,
    banners: ROUTES.BANNERS,
    'challenges-list': ROUTES.CHALLENGES,
    'challenges-locations': ROUTES.GPS_LOCATIONS,
    'challenges-submissions': ROUTES.CHALLENGES,
    'challenges-wallets': ROUTES.WALLETS,
    'challenges-payouts': ROUTES.PAYOUTS,
    'challenges-transactions': ROUTES.TRANSACTIONS,
  };

  const selectedKey =
    routeToKey[location.pathname] ??
    (location.pathname.startsWith('/users/') ? 'users' : null) ??
    (location.pathname.startsWith('/content/trending') ? 'content-trending' : null) ??
    (location.pathname.startsWith('/content/posts/') ? 'content-posts' : null) ??
    (location.pathname.startsWith('/tribes/') ? 'tribes' : null) ??
    (location.pathname.startsWith('/reports/') ? 'reports' : null) ??
    (location.pathname.startsWith('/support/') ? 'support' : null) ??
    (location.pathname.startsWith('/policies/') ? 'policies' : null) ??
    (location.pathname.startsWith('/challenges/') ? 'challenges-list' : null) ??
    (location.pathname.startsWith('/referrals/') ? 'referrals' : null) ??
    (location.pathname.startsWith('/admins/') ? 'admins-list' : null) ??
    (location.pathname.startsWith('/roles/') ? 'roles' : null) ??
    'dashboard';

  const openKeys = (() => {
    const keys: string[] = [];
    if (location.pathname.startsWith('/content/')) keys.push('content');
    if (location.pathname.startsWith('/notifications/')) keys.push('notifications');
    if (location.pathname.startsWith('/audit/')) keys.push('audit');
    if (location.pathname.startsWith('/settings/')) keys.push('settings');
    if (location.pathname.startsWith('/challenges')) keys.push('challenges');
    if (location.pathname.startsWith('/admins') || location.pathname.startsWith('/roles'))
      keys.push('admins');
    return keys;
  })();

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const path = keyToPath[key];
    if (path) navigate(path);
  };

  return (
    <Sider
      collapsible
      collapsed={sidebarCollapsed}
      trigger={null}
      theme="dark"
      width={240}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
      }}
    >
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: sidebarCollapsed ? 24 : 18,
          fontWeight: 700,
          letterSpacing: 2,
        }}
      >
        {sidebarCollapsed ? 'T' : 'TRIBE'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        defaultOpenKeys={openKeys}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ borderRight: 0 }}
      />
    </Sider>
  );
}
//Group visibility rules
